import type { TFunction } from 'i18next';
import type {
  AreaDecisionType,
  TeamPage,
  TeamStep,
  TeamTask,
} from '../../api/workEnvironmentTypes';
import { joinList } from '../HRDashboard/hmsFormat';
import { longDate } from '../Settings/privacyView';
import { monthAndYear } from '../WorkEnvironmentSurveyDetail/evaluationView';
import { teamLink } from '../WorkEnvironmentSurveyDetail/measuresView';
import {
  dayAndMonth,
  surveyLink,
} from '../WorkEnvironmentSurveyDetail/surveyProcessView';

const locale = (language: string) => (language === 'en' ? 'en-GB' : 'nb-NO');

/** «21.–28. september», or both months when they differ */
const dayRange = (from: string, to: string, language: string) => {
  const [start, end] = [new Date(from), new Date(to)];
  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();
  const startText = sameMonth
    ? new Intl.DateTimeFormat(locale(language), { day: 'numeric' }).format(
        start,
      )
    : `${dayAndMonth(from, language)} `;
  return `${startText}–${sameMonth ? '' : ' '}${dayAndMonth(to, language)}`;
};

const DETAILS: Record<
  TeamStep,
  (data: TeamPage, t: TFunction, language: string) => string | null
> = {
  survey: ({ survey }, t, language) =>
    t('workEnvironmentTeamPage.steps.surveyDetail', {
      responses: survey.responses,
      expected: survey.expected,
      period: dayRange(survey.from, survey.to, language),
    }),
  assessed: ({ assessment }, _t, language) =>
    assessment ? dayAndMonth(assessment.at, language) : null,
  proposals: ({ proposals }, t) =>
    t('workEnvironmentTeamPage.steps.proposalsDetail', {
      count: proposals.count,
      voters: proposals.voters,
      expected: proposals.expected,
    }),
  plan: ({ plans }, t, language) =>
    plans[0]
      ? t('workEnvironmentTeamPage.steps.planDetail', {
          date: dayAndMonth(plans[0].approvedAt, language),
        })
      : null,
  followUp: () => null,
  evaluation: ({ evaluationDate }, _t, language) =>
    evaluationDate ? longDate(evaluationDate, language) : null,
};

/** What happened in a step that is done, or «Nå» for the step the team is at */
export const stepDetail = (
  step: TeamStep,
  data: TeamPage,
  t: TFunction,
  language: string,
) => DETAILS[step](data, t, language);

/**
 * The team leader goes through the proposals before the team sees them, and makes the plan alone
 * without a safety representative
 */
const textKey = (step: TeamStep | 'done', data: Pick<TeamPage, 'measureStep' | 'safetyRep'>) => {
  if (step === 'proposals' && data.measureStep === 'review') return 'proposalsReview';
  return step === 'plan' && !data.safetyRep ? 'planNoRep' : step;
};

const currentStep = (data: Pick<TeamPage, 'steps'>) =>
  data.steps.find((step) => step.state === 'current')?.step ?? null;

/** «Teamet jobber med tiltaksplanen. Effekten måles med en ny undersøkelse i april 2027.» */
export const whereWeAre = (data: TeamPage, t: TFunction, language: string) => {
  const current = currentStep(data) ?? 'done';
  const now = t(`workEnvironmentTeamPage.now.${textKey(current, data)}`);
  return data.evaluationDate && current !== 'evaluation' && current !== 'done'
    ? `${now} ${t('workEnvironmentTeamPage.now.measured', { month: monthAndYear(data.evaluationDate, language) })}`
    : now;
};

export const measuresDone = (plan: TeamPage['plans'][number]) =>
  plan.measures.filter((measure) => measure.status === 'DONE').length;

/** Without a safety representative, the team leader gave the reason alone */
export const decisionText = (
  area: { decision: AreaDecisionType; deadline: string | null },
  data: Pick<TeamPage, 'safetyRep'>,
  t: TFunction,
  language: string,
) => {
  if (area.decision === 'PLAN_LATER' && area.deadline)
    return t('workEnvironmentTeamPage.decisions.PLAN_LATER_BY', { date: dayAndMonth(area.deadline, language) });
  const decision = area.decision === 'NOT_NOW' && !data.safetyRep ? 'NOT_NOW_NO_REP' : area.decision;
  return t(`workEnvironmentTeamPage.decisions.${decision}`);
};

/**
 * Why the sensitive areas are shielded, and where to speak up. Without a whistleblowing link,
 * the organisation's own routines. Without a safety representative, HR.
 */
export const shieldedTexts = (
  data: Pick<TeamPage, 'safetyRep' | 'whistleblowingUrl'>,
  areas: string,
  t: TFunction,
) => {
  const noRep = data.safetyRep ? '' : 'NoRep';
  return {
    why: t(`workEnvironmentTeamPage.result.why${noRep}`, { areas }),
    report: t(`workEnvironmentTeamPage.result.report${data.whistleblowingUrl ? '' : 'NoLink'}${noRep}`),
  };
};

/** «Trakassering og vold og trusler» */
export const shieldedNames = (areas: TeamPage['areas'], t: TFunction) =>
  joinList(
    areas
      .filter((area) => area.level === null)
      .map((area, index) =>
        index === 0 ? area.name : area.name.toLowerCase(),
      ),
    t,
    'and',
  );

const TASK_LINKS: Record<TeamTask, (origin: string, uuid: string) => string> = {
  propose: teamLink,
  highlight: teamLink,
  answer: surveyLink,
};

/** The page opens after the risk is assessed, so the team never waits in the survey or the assessment */
const WAITING_STEPS: TeamStep[] = ['proposals', 'plan', 'followUp', 'evaluation'];

/** The summary above already says who reviews the proposals or makes the plan, so the step only says the team can wait */
const NAMED_IN_SUMMARY = ['proposalsReview', 'plan', 'planNoRep'];

const waitKey = (key: string) => (NAMED_IN_SUMMARY.includes(key) ? 'nothing' : key);

type NextStep = {
  text: string;
  deadline: string | null;
  link: { label: string; href: string } | null;
};

/**
 * What the team does in the step it is at: a link to the page while it can act, or who acts
 * while it waits. Null when the work is done.
 */
export const nextStep = (
  data: Pick<TeamPage, 'steps' | 'measureStep' | 'task' | 'safetyRep'>,
  t: TFunction,
  language: string,
  origin: string,
): NextStep | null => {
  if (data.task) {
    const { task, uuid, deadline } = data.task;
    return {
      text: t(`workEnvironmentTeamPage.next.${task}.text`),
      deadline: deadline
        ? t('workEnvironmentTeamPage.next.deadline', {
            date: dayAndMonth(deadline, language),
          })
        : null,
      link: {
        label: t(`workEnvironmentTeamPage.next.${task}.link`),
        href: TASK_LINKS[task](origin, uuid),
      },
    };
  }
  const current = currentStep(data);
  return current && WAITING_STEPS.includes(current)
    ? {
        text: t(`workEnvironmentTeamPage.next.wait.${waitKey(textKey(current, data))}`),
        deadline: null,
        link: null,
      }
    : null;
};
